/* eslint max-classes-per-file: 0 */
/* eslint class-methods-use-this: 0 */

import { Store } from 'vuex'
import {
  Actions, Context, createMapper, Getters, Module, Mutations,
} from 'vuex-smart-module'
import { memoizeAsync } from 'utils-decorators'
import { LkTopic } from '@/typings/api/Topic'
import { LkPublication } from '@/typings/api/Publication'
import { NotificationBanner, NotificationBannerCollection } from '@/typings/api/NotificationBanner'

import {
  LkMunMehApi, LkRegMehApi,
  LkNotificationsBannersApi, LkPublicationsApi, LkTopicsApi, RoadmapsFeedApi,
} from '@/api/resources'
import { LkDataCollectionsApi } from '@/api/resources/LkDataCollectionsApi'
import { authStoreModule } from '@/views/Auth/store'
import { lkRoadmapPermissions as Permissions } from '@/helpers/enums/permission-models'
import { LkPublicTopicsApi } from '@/api/resources/public/LkPublicTopicsApi'

type S = LKState
type G = LKGetters
type M = LKMutations
type A = LKActions

class LKState {
  public topics: LkTopic[] = [];

  public banners: NotificationBannerCollection[] = [];

  public newBannersCount = 0;

  public roadmapsCount = 0;

  public regMehCount = 0;

  public munMehCount = 0;

  public isLoadingRegMehCount = false;

  public isLoadingMunMehCount = false;

  public isLoadingRoadmapsCount = false;

  public default_publication: LkPublication | null = null;

  public isAutoCurator: boolean | null = null;
}

class LKGetters extends Getters<S> {
  private store!: Store<unknown>;

  private auth!: Context<typeof authStoreModule>;

  public $init(store: Store<unknown>) {
    this.store = store
    this.auth = authStoreModule.context(store)
  }

  public get profile() {
    return this.auth.getters.profile
  }

  public get authorized() {
    return this.auth.getters.authorized
  }

  public get $access() {
    return this.store.$router.app.$access
  }

  public get isCurator() {
    return (
      this.getters.$access(Permissions.review)
      || this.getters.profile?.is_superuser
    )
  }

  public get isAdresat() {
    return (
      this.getters.$access(Permissions.report)
      && !this.getters.profile?.is_superuser
    )
  }

  public get isCoordinator() {
    if (this.getters.isCurator || this.getters.isAdresat) return false

    return (
      this.getters.$access(Permissions.read)
      || this.getters.profile?.is_federal_level
    )
  }

  public get isExpert() {
    return this.getters.$access(Permissions.expert) || this.getters.profile?.is_superuser
  }

  public get isPublicationCurator() {
    const list = this.getters.profile?.publications_curator
    return Array.isArray(list) && list.length > 0
  }

  public get hasDataCollectionAccess() {
    return this.getters.isPublicationCurator || this.state.isAutoCurator === true
  }

  public get isMunicipalCoordinator() {
    const permissionsMap = this.getters.profile?.permissions?.[Permissions.read]
    const schools = permissionsMap?.schools || []

    return (
      this.getters.isCoordinator
      && schools.length > 0
    )
  }

  public get isRegionCoordinator() {
    const permissionsMap = this.getters.profile?.permissions?.[Permissions.read]
    const municipalities = permissionsMap?.municipalities || []

    return (
      this.getters.isCoordinator
      && municipalities?.length > 0
    )
  }

  public get isFederalCoordinator() {
    const { profile } = this.getters
    if (!this.getters.isCoordinator || !profile) return false

    const permissionsMap = profile.permissions?.[Permissions.read]
    const regions = permissionsMap?.regions || []

    return (
      profile.is_superuser
      || profile.is_federal_level
      || regions.length > 0
    )
  }
}

class LKMutations extends Mutations<S> {
  public setTopics(payload: LkTopic[]) {
    this.state.topics = payload
  }

  public setBanners(payload: NotificationBannerCollection[]) {
    this.state.banners = payload
  }

  public setNewBannersCount(payload: number) {
    this.state.newBannersCount = Math.max(0, payload)
  }

  public decreaseUnreadTopicCount(slug_: LkTopic['slug']) {
    const idx = this.state.topics.findIndex(({ slug }) => slug === slug_)
    if (idx >= 0) {
      this.state.topics[idx].not_viewed_publications_count -= 1
    }
  }

  public setRoadmapsCount(payload: number) {
    this.state.roadmapsCount = payload
  }

  public setRoadmapsCountLoading(payload: boolean) {
    this.state.isLoadingRoadmapsCount = payload
  }

  public setMunMehCount(payload: number) {
    this.state.munMehCount = payload
  }

  public setRegMehCount(payload: number) {
    this.state.regMehCount = payload
  }

  public setMunMehCountLoading(payload: boolean) {
    this.state.isLoadingMunMehCount = payload
  }

  public setRegMehCountLoading(payload: boolean) {
    this.state.isLoadingRegMehCount = payload
  }

  public setDefaultPublication(payload: LkPublication) {
    this.state.default_publication = payload
  }

  public setIsAutoCurator(payload: boolean) {
    this.state.isAutoCurator = payload
  }

  public reset() {
    this.setTopics([])
    this.setMunMehCount(0)
    this.setRoadmapsCount(0)
    this.state.isAutoCurator = null
  }
}

class LKActions extends Actions<S, G, M, A> {
  private readonly topicsApi = new LkTopicsApi();

  private readonly publicTopicsApi = new LkPublicTopicsApi();

  private readonly lkNotificationsBannersApi = new LkNotificationsBannersApi();

  private readonly lkPublicationsApi = new LkPublicationsApi();

  private readonly roadmapsFeedApi = new RoadmapsFeedApi();

  private readonly lkDataCollectionsApi = new LkDataCollectionsApi();

  @memoizeAsync(10_000)
  public async getTopics() {
    if (this.state.topics.length > 0) {
      return this.state.topics
    }

    const { authorized } = this.getters
    let topicsList
    if (!authorized) {
      const { topics } = await this.publicTopicsApi.getAll({})
      topicsList = topics
    } else {
      const { topics } = await this.topicsApi.getAll({})
      topicsList = topics
    }
    if (topicsList) this.mutations.setTopics(topicsList)
    return this.state.topics
  }

  @memoizeAsync(1_000)
  public async getBanners({ slug }: { slug?: string } = {}) {
    const { authorized } = this.getters
    if (!authorized) {
      return []
    }
    const params = { publication_slug: slug }
    // @ts-ignore TODO:
    // eslint-disable-next-line max-len
    const { banners, not_viewed } = await this.lkNotificationsBannersApi.getAll(params) as { banners: NotificationBannerCollection[]; not_viewed: number }
    if (banners) this.mutations.setBanners(banners)
    if (not_viewed) {
      this.mutations.setNewBannersCount(not_viewed)
    } else {
      this.mutations.setNewBannersCount(0)
    }
    return this.state.banners
  }

  public async readBanner(id: NotificationBanner['id']) {
    const { authorized } = this.getters
    if (!authorized) {
      return
    }

    try {
      await this.lkNotificationsBannersApi.read(id)
      this.mutations.setNewBannersCount(this.state.newBannersCount - 1)
    } catch (e) {
      // Возвращается 304 при повторном просмотре и триггерит catch.
    }
  }

  public async readPublication({ data, slug }: {data: LkPublication; slug: LkTopic['slug']}) {
    const { authorized } = this.getters
    if (!authorized) {
      return true
    }

    try {
      if (!data.is_viewed) {
        await this.lkPublicationsApi.read(data.id)
        this.mutations.decreaseUnreadTopicCount(slug)
        return true
      }
    } catch (e) {
      // Возвращается 304 при повторном просмотре и триггерит catch.
    }
    return data.is_viewed
  }

  @memoizeAsync(10_000)
  public async getDefaultPublication() {
    const { authorized } = this.getters
    if (this.state.default_publication || !authorized) return
    const response = await this.lkPublicationsApi.getDefault()
    this.mutations.setDefaultPublication(response)
  }

  public async reloadTopics() {
    const { authorized } = this.getters
    let topicsList
    if (!authorized) {
      const { topics } = await this.publicTopicsApi.getAll({})
      topicsList = topics
    } else {
      const { topics } = await this.topicsApi.getAll({})
      topicsList = topics
    }
    if (topicsList) this.mutations.setTopics(topicsList)
    return this.state.topics
  }

  public async getRoadmapsCount() {
    const { roadmapsCount } = this.state
    if (roadmapsCount > 0) {
      return
    }

    const { authorized } = this.getters
    if (authorized) {
      this.mutations.setRoadmapsCountLoading(true)
      const { data } = await this.roadmapsFeedApi.getCount()
      if (data) this.mutations.setRoadmapsCount(data.roadmaps_count)
      // this.mutations.setRoadmapsCount(0)
      this.mutations.setRoadmapsCountLoading(false)
    }
  }

  public async getMunMehCount() {
    const { munMehCount } = this.state
    if (munMehCount > 0) {
      return
    }

    const { authorized } = this.getters
    if (authorized) {
      this.mutations.setMunMehCountLoading(true)
      const { count } = await new LkMunMehApi().count()
      this.mutations.setMunMehCount(count)
      this.mutations.setMunMehCountLoading(false)
    }
  }

  public async getRegMehCount() {
    const { regMehCount } = this.state
    if (regMehCount > 0) {
      return
    }

    const { authorized } = this.getters
    if (authorized) {
      this.mutations.setRegMehCountLoading(true)
      const { count } = await new LkRegMehApi().count()
      this.mutations.setRegMehCount(count)
      this.mutations.setRegMehCountLoading(false)
    }
  }

  public async checkIsAutoCurator() {
    if (this.state.isAutoCurator !== null) {
      return this.state.isAutoCurator
    }

    const { authorized } = this.getters
    if (!authorized) {
      this.mutations.setIsAutoCurator(false)
      return false
    }

    try {
      const response = await this.lkDataCollectionsApi.isAutoCurator()
      const result = response.data || false
      this.mutations.setIsAutoCurator(result)
      return result
    } catch (error) {
      this.mutations.setIsAutoCurator(false)
      return false
    }
  }
}

const storeModule = (
  new Module({
    state: LKState,
    getters: LKGetters,
    mutations: LKMutations,
    actions: LKActions,
  })
)

const mapper = createMapper(storeModule)

export {
  storeModule as lkStoreModule,
  mapper as lkMapper,
}
